import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  copySessionOnto,
  copyWeekOnto,
  countActiveInWeek,
  duplicateSession,
  duplicateWeekAt,
  MAX_CYCLE_WEEKS,
  MAX_SESSIONS,
  moveSession,
  moveWeekAt,
  sessionExerciseCount,
  removeRoundByRules,
  roundTypeAt,
  setRoundTypeByRules,
  updateSession,
  type Draft,
  type RoundType,
  programRoundLabel,
} from "@heraion/shared";

import type { ProgramEditorV3Props } from "./types";
import { ruleOptions } from "./ruleCache";
import { roundLockOf } from "./useRoundLock";
import type { V3Navigation } from "./useV3Navigation";

type Args = Pick<
  ProgramEditorV3Props,
  "draft" | "setDraft" | "confirm" | "structure" | "showToast" | "onUndo"
> & {
  nav: V3Navigation;
  /** Rounds 0..lockedThrough were executed: their type stays, they stay. */
  lockedThrough?: number;
  /** useRoundLock's own test; default, from lockedThrough. */
  isRoundLocked?: (week: number) => boolean;
  /** The cells' unit: a scaled load rounds to its step. Default kg. */
  cellUnit?: ProgramEditorV3Props["cellUnit"];
  /** Says a locked round is refused (useRoundLock, with «Unlock»). */
  refuse?: (week: number) => void;
};

/** False for an empty name or a default day code ("D3"). */
const hasOwnName = (name: string | undefined): name is string =>
  !!name && !/^D\d+$/i.test(name);

/** "D2", or "D2 Push" when the day has a name. A name that is the code itself
 *  ("D2", the default of a new program) or starts with it ("D2 Push") is not
 *  prefixed again. */
export const dayLabel = (draft: Draft, sIdx: number): string => {
  const code = `D${sIdx + 1}`;
  const name = draft.sessions[sIdx]?.name.trim();
  // Empty, or a default code ("D3", also stale after a move): the position speaks.
  if (!hasOwnName(name)) return code;
  const lower = name.toLowerCase();
  const own = code.toLowerCase();
  if (lower.startsWith(`${own} `)) return name;
  return `${code} ${name}`;
};

// The day menu's writes (#336, 2A actions): duplicate, copy onto, move,
// rename, clear, remove, add. And the round menu's (3b): duplicate right
// after, copy onto, move earlier or later, remove, and its type (4d). Destructive ones confirm when something would
// be lost and offer Undo in a toast afterwards.
export function useV3DayActions({
  draft,
  setDraft,
  confirm,
  structure,
  showToast,
  onUndo,
  nav,
  lockedThrough = -1,
  isRoundLocked,
  cellUnit = "kg",
  refuse,
}: Args) {
  const { t } = useTranslation();
  const undo = { label: t("program.undo", { defaultValue: "Undo" }), onPress: onUndo };
  const cancelLabel = t("program.cancel", { defaultValue: "Cancel" });
  const lockTest = useMemo(() => isRoundLocked ?? roundLockOf(lockedThrough), [isRoundLocked, lockedThrough]);
  // An executed round is not rewritten (slice 5, §5.2.4): said, not done.
  const isLocked = useCallback(
    (week: number) => {
      if (!lockTest(week)) return false;
      if (refuse) refuse(week);
      else showToast(t("editorV3.roundLocked", { n: week + 1, defaultValue: "W{{n}} is done: it stays as it is." }));
      return true;
    },
    [lockTest, refuse, showToast, t],
  );

  // Confirm only when exercises would go: an empty day loses nothing.
  const guarded = (count: number, ask: { title: string; body: string; ok: string }, run: () => void) => {
    if (count === 0) return run();
    confirm({ title: ask.title, body: ask.body, confirmLabel: ask.ok, cancelLabel }).then(
      (yes) => {
        if (yes) run();
      },
    );
  };

  const duplicate = (sIdx: number) => {
    const src = draft.sessions[sIdx]?.name.trim();
    // A day with no name of its own gives the copy none either: its default
    // code, so it reads "D2", not "D2 D1 (copy)". Not "": the save refuses it.
    const name = hasOwnName(src)
      ? t("editorV3.copyName", { name: src, defaultValue: "{{name}} (copy)" })
      : `D${sIdx + 2}`;
    setDraft((d) => {
      const next = duplicateSession(d, sIdx);
      if (next === d) return d;
      return updateSession(next, sIdx + 1, { name });
    });
    nav.selectDay(sIdx + 1);
    showToast(t("editorV3.dayDuplicated", { defaultValue: "Day duplicated" }), undo);
  };

  const copyOnto = (from: number, to: number) => {
    const target = dayLabel(draft, to);
    const source = dayLabel(draft, from);
    const count = sessionExerciseCount(draft, to);
    guarded(
      count,
      {
        title: t("editorV3.copyOntoTitle", { day: target, defaultValue: "Replace {{day}}?" }),
        body: t("editorV3.copyOntoBody", {
          count,
          day: target,
          from: source,
          defaultValue: "{{day}} has {{count}} exercises. A copy of {{from}} replaces them.",
        }),
        ok: t("editorV3.replace", { defaultValue: "Replace" }),
      },
      () => {
        setDraft((d) => copySessionOnto(d, from, to));
        showToast(
          t("editorV3.dayCopied", { day: target, defaultValue: "Copied onto {{day}}" }),
          undo,
        );
      },
    );
  };

  const move = (sIdx: number, delta: -1 | 1) => {
    setDraft((d) => moveSession(d, sIdx, delta));
    nav.selectDay(sIdx + delta);
  };

  const rename = (sIdx: number, name: string) =>
    setDraft((d) => updateSession(d, sIdx, { name }));

  const clear = (sIdx: number) => {
    const day = dayLabel(draft, sIdx);
    guarded(
      sessionExerciseCount(draft, sIdx),
      {
        title: t("editorV3.clearTitle", { day, defaultValue: "Clear {{day}}?" }),
        body: t("editorV3.clearBody", {
          count: sessionExerciseCount(draft, sIdx),
          defaultValue: "{{count}} exercises will be removed.",
        }),
        ok: t("editorV3.clear", { defaultValue: "Clear day" }),
      },
      () => {
        setDraft((d) => updateSession(d, sIdx, { slots: [] }));
        nav.closeExercise();
        showToast(t("editorV3.dayCleared", { day, defaultValue: "{{day}} cleared" }), undo);
      },
    );
  };

  const remove = (sIdx: number) => {
    if (draft.sessions.length <= 1) return;
    const day = dayLabel(draft, sIdx);
    const count = sessionExerciseCount(draft, sIdx);
    confirm({
      title: t("editorV3.removeDayTitle", { day, defaultValue: "Remove {{day}}?" }),
      body:
        count > 0
          ? t("editorV3.removeDayBody", {
              count,
              defaultValue: "The day goes with its {{count}} exercises.",
            })
          : t("editorV3.removeDayEmptyBody", { defaultValue: "The day is empty." }),
      confirmLabel: t("editorV3.remove", { defaultValue: "Remove" }),
      cancelLabel,
    }).then((yes) => {
      if (yes) removeConfirmed(sIdx);
    });
  };

  // Also the heatmap's hold on a day, which has asked already.
  const removeConfirmed = (sIdx: number) => {
    const day = dayLabel(draft, sIdx);
    structure.removeDay(sIdx);
    nav.selectDay(Math.max(0, sIdx - 1));
    showToast(t("editorV3.dayRemoved", { day, defaultValue: "{{day}} removed" }), undo);
  };

  const add = () => {
    if (draft.sessions.length >= MAX_SESSIONS) return;
    structure.addDay(`D${draft.sessions.length + 1}`);
    nav.selectDay(draft.sessions.length);
  };

  // W+ (heatmap and wide grid): an empty round at the end. The heatmap's hold
  // on W+ repeats round `from` there instead. Null when the cycle is full.
  const canAddRound = draft.cycleWeeks < MAX_CYCLE_WEEKS;
  const addRound = canAddRound ? () => structure.changeCycleWeeks(draft.cycleWeeks + 1, "blank") : undefined;
  const repeatRound = canAddRound ? (from: number) => structure.addWeekRepeating(from) : undefined;

  // The heatmap's hold on a round header already confirmed; this adds the
  // undo. The rules follow (slice 5): the cells by hand after it move with
  // their round, the rest is written again.
  const removeRound = useCallback(
    (week: number) => {
      if (isLocked(week)) return;
      setDraft((d) => removeRoundByRules(d, week, { ...ruleOptions(cellUnit), lockedThrough }));
      showToast(
        t("editorV3.roundRemovedByRules", {
          n: week + 1,
          defaultValue: "W{{n}} removed. Cells by hand after it move with their round.",
        }),
        { label: t("program.undo", { defaultValue: "Undo" }), onPress: onUndo },
      );
    },
    [setDraft, showToast, t, onUndo, isLocked, lockedThrough, cellUnit],
  );

  // The round menu. The cursor follows the round it acted on.
  const duplicateRound = (week: number) => {
    setDraft((d) => duplicateWeekAt(d, week));
    nav.selectRound(week + 1);
    showToast(t("editorV3.roundDuplicated", { n: week + 1, defaultValue: "W{{n}} duplicated" }), undo);
  };

  // Same confirm as the day's copy: only when the target has exercises.
  const copyRoundOnto = (from: number, to: number) => {
    const count = countActiveInWeek(draft, to);
    const target = programRoundLabel(to + 1);
    const source = programRoundLabel(from + 1);
    guarded(
      count,
      {
        title: t("editorV3.copyRoundTitle", { round: target, defaultValue: "Replace {{round}}?" }),
        body: t("editorV3.copyRoundBody", {
          count,
          round: target,
          from: source,
          defaultValue: "{{round}} has {{count}} exercises across the days. A copy of {{from}} replaces them.",
        }),
        ok: t("editorV3.replace", { defaultValue: "Replace" }),
      },
      () => {
        setDraft((d) => copyWeekOnto(d, from, to));
        nav.selectRound(to);
        showToast(t("editorV3.roundCopied", { round: target, defaultValue: "Copied onto {{round}}" }), undo);
      },
    );
  };

  const moveRound = (week: number, delta: -1 | 1) => {
    setDraft((d) => moveWeekAt(d, week, delta));
    nav.selectRound(week + delta);
  };

  // #336 S2: the round menu's «Type», for the whole program (§8.3): every
  // exercise gets the effect from its rule (slice 5).
  const setRoundTypeAt = (week: number, type: RoundType, word: string) => {
    if (roundTypeAt(draft, week) === type || isLocked(week)) return;
    setDraft((d) => setRoundTypeByRules(d, week, type, { ...ruleOptions(cellUnit), lockedThrough }));
    showToast(
      t("editorV3.roundTypeSet", {
        n: week + 1,
        type: word,
        defaultValue: "W{{n}} {{type}}. Applied to: the whole program.",
      }),
      undo,
    );
  };

  const removeRoundAsked = (week: number) => {
    if (isLocked(week)) return;
    confirm({
      title: t("editorV3.removeRoundTitle", { n: week + 1, defaultValue: "Remove W{{n}}?" }),
      body: t("editorV3.removeRoundBody", {
        defaultValue: "Its sets leave every day. The rounds after it move one place earlier.",
      }),
      confirmLabel: t("editorV3.remove", { defaultValue: "Remove" }),
      cancelLabel,
    }).then((yes) => {
      if (yes) removeRound(week);
    });
  };

  return {
    duplicate,
    copyOnto,
    move,
    rename,
    clear,
    remove,
    removeConfirmed,
    add,
    addRound,
    repeatRound,
    removeRound,
    duplicateRound,
    copyRoundOnto,
    moveRound,
    removeRoundAsked,
    setRoundType: setRoundTypeAt,
  };
}

export type V3DayActions = ReturnType<typeof useV3DayActions>;
